import { useContext, useState, useEffect, act } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { config } from "../../../Environment";
import useAuth from "../../../hooks/useAuth";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import { WSContext } from "../../../App";
import LoggerContext from "../../../context/LoggerProvider";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { AUTHOR_ROLE } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ShoppingCart,
  Clock,
  CheckCircle,
  Gift,
} from "lucide-react";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { toast } from "sonner";
import BuySubscriptionModal from "../modals/buySubscription";
import { decimalAmount, currencyAmount } from "../../../lib/currency";

// vendorId only used for vendor-specific subscriptions (like memberships)
// without vendor id we retrieve only the platform subscriptions
function Platform({ vendorId }: { vendorId?: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const { socket } = useContext(WSContext);

  const roles = user.roles ?? [];
  const isAuthor = roles.includes(AUTHOR_ROLE);

  const [errMsg, setErrMsg] = useState("");
  const retryFetch = useRetryFetch();

  const [subscriptionProducts, setSubscriptionProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [buySubscriptionModalVisible, setBuySubscriptionModalVisible] = useState<boolean>(false);
  const [productInfo, setProductInfo] = useState<any>(null);

  async function loadSubscriptionProducts() {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set('type', 'subscription');
      if (vendorId) {
        query.set('vendorId', vendorId);
      } else {
        query.set('platform', '1');
      }
      const response = await retryFetch(`${config.API_BASE}/api/product/searchActive?${query.toString()}`);
      const result = await response.json();
      if (Array.isArray(result.list)) {
        setSubscriptionProducts(result.list);
      }
    } finally {
      setLoading(false);
    }
  }

  async function buySubscription(idx: number) {
    setProductInfo(subscriptionProducts[idx]);
    setBuySubscriptionModalVisible(true);
  }

  useEffectOnce(() => {
    loadSubscriptionProducts();
  }, []);

  /*
  // format is purchase price (if > 0) + recurring price / interval
  function renderPrice(i: number) {
    const purchasePrice = subscriptionProducts[i].price;
    const recurringPrice = subscriptionProducts[i].data.subscription.recurringPrice;
    const parts = [];
    if (purchasePrice.amount) {
      parts.push(currencyAmount(purchasePrice.amount, purchasePrice.currency));
    }
    parts.push(currencyAmount(recurringPrice.amount, recurringPrice.currency) + "/" + t(recurringPrice.interval));
    const text = parts.join(" + ");
    return text;
  }
  */
  function renderPrice(i: number) {
    const recurringPrice = subscriptionProducts[i].data.subscription.recurringPrice;
    return currencyAmount(recurringPrice.amount, recurringPrice.currency) + "/" + t(recurringPrice.interval);
  }

  function renderRoleBadges(roles: string[]) {
    if (!Array.isArray(roles) || roles.length === 0) {
      return null;
    }

    const roleLabels: { [key: string]: string } = {
      author: t("author"),
      expert: t("expert"),
      vendor: t("vendor"),
    };

    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {roles.map((role) => (
          <Badge key={role} variant="secondary" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            {roleLabels[role] || role}
          </Badge>
        ))}
      </div>
    );
  }

  function renderCategoriesAndTags(product: any) {
    const categories = Array.isArray(product.categories) ? product.categories : [];
    const tags = Array.isArray(product.tags) ? product.tags : [];

    if (categories.length === 0 && tags.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {categories.map((cat: any) => {
          const isObject = typeof cat === 'object' && cat !== null;
          return (
            <Badge
              key={isObject ? cat._id : cat}
              style={{
                backgroundColor: isObject ? cat.color : '#e5e7eb',
                color: isObject ? (cat.textColor || '#000') : '#000',
              }}
            >
              {isObject ? cat.name : cat}
            </Badge>
          );
        })}
        {tags.map((tag: any) => {
          const isObject = typeof tag === 'object' && tag !== null;
          return (
            <Badge
              key={isObject ? tag._id : tag}
              variant="outline"
              style={{
                borderColor: isObject ? tag.color : '#d1d5db',
                color: isObject ? tag.color : '#6b7280',
              }}
            >
              {isObject ? tag.name : tag}
            </Badge>
          );
        })}
      </div>
    );
  }

  function renderSubscriptionList() {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array(3)
            .fill(0)
            .map((_, index) => (
              <Card key={index} className="flex flex-col">
                <CardHeader>
                  <Skeleton className="h-40 w-full rounded bg-gray-200 mb-4" />
                  <Skeleton className="h-6 w-3/4 rounded bg-gray-200 mb-2" />
                  <Skeleton className="h-4 w-full rounded bg-gray-200" />
                  <Skeleton className="h-4 w-5/6 rounded bg-gray-200" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-32 rounded bg-gray-200" />
                </CardContent>
              </Card>
            ))}
        </div>
      );
    }

    if (subscriptionProducts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium mb-1">
            {t("marketplace_unavailable") || "No subscriptions available"}
          </p>
          <p className="text-gray-500 text-sm">
            {t("check_back_later") || "Check back later for new subscriptions"}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {subscriptionProducts.map((product, i) => {
          const recurringPrice = product.data?.subscription?.recurringPrice;
          const roles = product.data?.subscription?.roles || [];
          const freeTrial = product.data?.subscription?.freeTrial;
          const hasFreeTrial = freeTrial && freeTrial.length > 0;

          return (
            <Card
              key={product._id}
              className="flex flex-col hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/marketplace/subscription/${product._id}`)}
            >
              {/* Product Image */}
              {product.imageUrl && (
                <div className="w-full h-48 overflow-hidden rounded-t-lg">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              <CardHeader className="flex-grow">
                <CardTitle className="text-xl">{product.name}</CardTitle>
                {product.description && (
                  <CardDescription className="text-sm mt-2">
                    {product.description}
                  </CardDescription>
                )}

                {/* Categories and Tags */}
                {/*{renderCategoriesAndTags(product)}*/}

                {/* Roles Granted */}
                {roles.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t("grants_access_to") || "Grants access to"}:
                    </p>
                    {renderRoleBadges(roles)}
                  </div>
                )}

                {/* Free Trial Badge */}
                {hasFreeTrial && (
                  <div className="mt-3">
                    <Badge variant="secondary" className="bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                      <Gift className="h-3 w-3" />
                      {freeTrial.length} {t(freeTrial.interval)} {t("free_trial") || "free trial"}
                    </Badge>
                  </div>
                )}
              </CardHeader>

              <CardFooter className="flex items-center justify-between border-t pt-4">
                <div className="flex flex-col">
                  <span className="text-2xl font-bold text-primary">
                    {recurringPrice && currencyAmount(recurringPrice.amount, recurringPrice.currency)}
                  </span>
                  <span className="text-sm text-gray-500">
                    / {recurringPrice && t(recurringPrice.interval)}
                  </span>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    buySubscription(i);
                  }}
                  className="flex items-center gap-2"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {t("subscribe") || t("buy")}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    );
  }

  function renderBuySubscriptionModal() {
    if (buySubscriptionModalVisible) {
      return (
        <BuySubscriptionModal
          productInfo={productInfo}
          buySubscriptionModalVisible={buySubscriptionModalVisible}
          setBuySubscriptionModalVisible={setBuySubscriptionModalVisible}
        />
      );
    } else {
      return (<></>);
    }
  }

  return <div>
    <div>{errMsg ? <div className="error">{errMsg}</div> : ""}</div>
    {renderSubscriptionList()}

        {/* : Array(Math.floor(Math.random() * 10 + 5))
            .join(".")
            .split(".")
            .map((_, arrIdx) => {
                return (
                <TableRow key={arrIdx}>
                    <TableCell>
                    <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                    </TableCell>
                    <TableCell>
                    <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                    </TableCell>
                </TableRow>
                );
            })} */}

    {renderBuySubscriptionModal()}

    </div>;
}

export default Platform;
